import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Button } from "@/components/ui/button"
import { Link } from "react-router-dom"
import { ArrowRight } from "lucide-react"
import { JourneyTimeline } from "@/components/JourneyTimeline"
import { PageFAQ } from "@/components/PageFAQ"
import { aboutFAQs } from "@/data/faqData"

import { SEO } from "@/components/SEO"

const About = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900 selection:bg-[#FDA400]/30">
      <SEO
        title="Architecting the Cognitive Future | RSA Tech Group"
        description="We are not just a technology company. We are the architects of the cognitive enterprise, bridging the gap between human ingenuity and artificial intelligence."
        image="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072"
      />
      <Navigation />

      {/* Hero Section */}
      <section className="relative bg-white pt-32 md:pt-40 pb-0 overflow-hidden">
        <div className="section-container">
          {/* Two-column: text left, image right */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: text */}
            <div className="py-8 md:py-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-10 bg-[#FDA400]" />
                <span className="text-[#1C2B6E] text-xs font-semibold uppercase tracking-[0.2em]">
                  About RSA Tech Group
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#1C2B6E] tracking-tight leading-[1.1] mb-6">
                We build systems
                <br />
                that <span className="text-[#FDA400]">endure.</span>
              </h1>

              <p className="text-lg text-slate-500 leading-relaxed font-light max-w-lg mb-10">
                RSA Tech Group is the AI holding company building the next generation of intelligent enterprise companies. We invest in, incubate, and scale AI-native businesses that transform industries.
              </p>

              {/* Stats row */}
              <div className="flex gap-10 mb-10">
                {[
                  { value: "2012", label: "Founded" },
                  { value: "50+", label: "Clients" },
                  { value: "24/7", label: "Operations" },
                ].map((stat, i) => (
                  <div key={i}>
                    <div className="text-3xl md:text-4xl font-black text-[#1C2B6E]">{stat.value}</div>
                    <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <Link to="/contact">
                  <Button className="bg-[#1C2B6E] hover:bg-[#162258] text-white font-semibold text-sm px-7 py-5 h-auto rounded-lg transition-all">
                    Get in Touch
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/leadership-team">
                  <Button variant="outline" className="font-semibold text-sm px-7 py-5 h-auto rounded-lg border-[#1C2B6E]/20 text-[#1C2B6E] hover:bg-[#1C2B6E]/5 transition-all">
                    Meet the Team
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: image collage */}
            <div className="relative hidden lg:block">
              <div className="grid grid-cols-2 gap-4 pr-4">
                {/* Top-left: tall image */}
                <div className="rounded-2xl overflow-hidden h-[340px] mt-8">
                  <img
                    src="https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=600&q=80"
                    alt="Team strategy session"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Top-right: shorter image */}
                <div className="rounded-2xl overflow-hidden h-[280px]">
                  <img
                    src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=600&q=80"
                    alt="Team collaboration"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Bottom-left: shorter image */}
                <div className="rounded-2xl overflow-hidden h-[280px]">
                  <img
                    src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=600&q=80"
                    alt="Client meeting"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Bottom-right: tall image */}
                <div className="rounded-2xl overflow-hidden h-[340px] -mt-8">
                  <img
                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=600&q=80"
                    alt="Engineering team"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom edge fade into next section */}
        <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent mt-12" />
      </section>


      {/* Commitment to Customers */}
      <section className="relative py-28 overflow-hidden bg-[#0f1629]">
        {/* Background layers */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=2000"
            alt=""
            className="w-full h-full object-cover opacity-[0.07]"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0f1629] via-[#0f1629]/95 to-[#1C2B6E]/30" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#1C2B6E]/[0.08] rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#FDA400]/[0.04] rounded-full blur-[120px]" />
        </div>

        <div className="section-container relative z-10">
          {/* Top: heading + subtitle */}
          <div className="max-w-3xl mb-16">
            <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
              Our Promise
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6">
              Our Commitment <br />
              <span className="text-[#FDA400]">
                To You
              </span>
            </h2>
            <p className="text-slate-400 text-lg md:text-xl leading-relaxed font-light">
              We operate as a true Managed Solutions Partner, moving beyond transactional delivery to strategic ownership of your digital portfolio.
            </p>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Card 1: Outcome Ownership - tall */}
            <div className="group relative rounded-2xl overflow-hidden bg-white/[0.04] border border-white/[0.06] p-8 md:p-10 hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-500 lg:row-span-2">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="text-7xl md:text-8xl font-black text-white/[0.04] absolute -top-2 -right-2 leading-none select-none group-hover:text-white/[0.08] transition-colors duration-500">
                01
              </span>
              <div className="relative z-10 h-full flex flex-col">
                <div className="text-sm font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-6">01</div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 tracking-tight">
                  Outcome Ownership
                </h3>
                <p className="text-slate-400 leading-relaxed mb-6 flex-1">
                  We transition from effort-based metrics to value-based outcomes, sharing the risk and reward of your digital transformation. Every initiative is measured against business impact, not just deliverables.
                </p>
                <div className="mt-auto pt-6 border-t border-white/[0.06]">
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="text-3xl font-bold text-white">100%</div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider">Accountability</div>
                    </div>
                    <div className="w-px h-10 bg-white/10" />
                    <div>
                      <div className="text-3xl font-bold text-white">10+</div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider">Years Avg</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Operational Excellence */}
            <div className="group relative rounded-2xl overflow-hidden bg-white/[0.04] border border-white/[0.06] p-8 hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-500">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="text-7xl font-black text-white/[0.04] absolute -top-2 -right-2 leading-none select-none group-hover:text-white/[0.08] transition-colors duration-500">
                02
              </span>
              <div className="relative z-10">
                <div className="text-sm font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-6">02</div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3 tracking-tight">
                  Operational Excellence
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Enterprise-grade rigor, automation, and governance across every layer of your technology stack. We engineer stability you can build on.
                </p>
              </div>
            </div>

            {/* Card 3: Image card */}
            <div className="group relative rounded-2xl overflow-hidden min-h-[220px]">
              <img
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984?q=80&w=800"
                alt="Team collaboration"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f1629] via-[#0f1629]/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                <p className="text-white/90 text-sm font-medium leading-relaxed">
                  Our teams integrate directly with yours, operating as a seamless extension of your engineering organization.
                </p>
              </div>
            </div>

            {/* Card 4: Enduring Partnership */}
            <div className="group relative rounded-2xl overflow-hidden bg-white/[0.04] border border-white/[0.06] p-8 hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-500">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="text-7xl font-black text-white/[0.04] absolute -top-2 -right-2 leading-none select-none group-hover:text-white/[0.08] transition-colors duration-500">
                03
              </span>
              <div className="relative z-10">
                <div className="text-sm font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-6">03</div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3 tracking-tight">
                  Enduring Partnership
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  We build for the decade, not just the quarter. Your technology investments yield sustainable, long-term competitive advantage.
                </p>
              </div>
            </div>

            {/* Card 5: Quote/stat card */}
            <div className="group relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#1C2B6E] to-[#0f1a4a] p-8 flex flex-col justify-center">
              <div className="absolute top-4 left-6 text-6xl text-white/10 font-serif leading-none">"</div>
              <p className="text-white/90 text-lg md:text-xl font-light leading-relaxed italic relative z-10">
                We replace fragmentation with cohesion, building intelligent systems that let you focus on growth.
              </p>
              <div className="mt-4 flex items-center gap-3 relative z-10">
                <div className="w-px h-5 bg-white/30" />
                <span className="text-white/60 text-xs uppercase tracking-widest font-medium">RSA Tech Group Philosophy</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Evolution Timeline */}
      <JourneyTimeline />

      {/* Global Delivery Process (Enhanced) */}
      <section className="py-28 relative overflow-hidden bg-[#0f1629] text-white">
        {/* Deep space background layers */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_40%,rgba(28,43,110,0.15),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_30%_70%,rgba(253,164,0,0.04),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_80%_30%,rgba(28,43,110,0.08),transparent_50%)]" />
        </div>

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <div className="section-container relative z-10">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-20">
            <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
              Worldwide Presence
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 text-white tracking-tight leading-[1.1]">
              Global Scale, <br />
              <span className="text-[#FDA400]">
                Local Precision
              </span>
            </h2>
            <p className="text-slate-400 text-lg font-light leading-relaxed">
              A distributed network of innovation hubs ensuring 24/7 velocity across continents.
            </p>
          </div>

          {/* Location Hub Cards */}
          <div className="grid md:grid-cols-2 gap-5 mb-16 max-w-4xl mx-auto">
            {[
              {
                city: "Texas, USA",
                label: "Headquarters",
                image: "https://images.unsplash.com/photo-1531218150217-54595bc2b934?auto=format&fit=crop&w=800&q=80",
                timezone: "CST (UTC-6)",
                focus: "Strategy, Sales, Client Success",
                stat: "HQ",
              },
              {
                city: "Hyderabad, India",
                label: "Engineering Center",
                image: "https://images.unsplash.com/photo-1600320254374-ce2d293c324e?auto=format&fit=crop&w=800&q=80",
                timezone: "IST (UTC+5:30)",
                focus: "Engineering, AI/ML, Managed Services",
                stat: "APAC",
              },
            ].map((hub, i) => (
              <div
                key={i}
                className="group relative rounded-2xl overflow-hidden h-[380px] cursor-default"
              >
                {/* City image */}
                <img
                  src={hub.image}
                  alt={hub.city}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f1629] via-[#0f1629]/70 to-[#0f1629]/20 group-hover:via-[#0f1629]/60 transition-colors duration-500" />

                {/* Pulsing dot - top right */}
                <div className="absolute top-5 right-5 z-10 flex items-center gap-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400" />
                  </span>
                  <span className="text-xs text-emerald-300 font-medium">Active</span>
                </div>

                {/* Region badge - top left */}
                <div className="absolute top-5 left-5 z-10">
                  <span className="text-[10px] font-bold text-[#FDA400] uppercase tracking-[0.25em] bg-[#FDA400]/15 border border-[#FDA400]/20 px-3 py-1 rounded-full backdrop-blur-sm">
                    {hub.stat}
                  </span>
                </div>

                {/* Content at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                  <div className="text-xs text-[#FDA400] uppercase tracking-[0.2em] font-semibold mb-2">
                    {hub.label}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight mb-4">
                    {hub.city}
                  </h3>

                  {/* Details row */}
                  <div className="flex flex-col gap-2 pt-4 border-t border-white/10">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-400 uppercase tracking-wider">Timezone</span>
                      <span className="text-sm text-white font-medium">{hub.timezone}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-400 uppercase tracking-wider">Focus</span>
                      <span className="text-sm text-white font-medium text-right max-w-[180px]">{hub.focus}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Connection line visual */}
          <div className="flex items-center justify-center gap-0 mb-16 max-w-4xl mx-auto px-8">
            <div className="h-3 w-3 rounded-full bg-[#FDA400] shadow-[0_0_12px_rgba(253,164,0,0.5)] shrink-0" />
            <div className="h-px flex-1" style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(253,164,0,0.4) 0px, rgba(253,164,0,0.4) 8px, transparent 8px, transparent 16px)" }} />
            <div className="h-3 w-3 rounded-full bg-[#FDA400] shadow-[0_0_12px_rgba(253,164,0,0.5)] shrink-0" />
          </div>

          {/* Capability cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                num: "01",
                title: "Global Reach",
                desc: "Delivery centers across North America, Europe, and Asia powering round-the-clock innovation.",
                stat: "3+",
                statLabel: "Continents",
              },
              {
                num: "02",
                title: "Precision",
                desc: "Agile methodologies tailored for enterprise needs, with localized engagement teams.",
                stat: "99.9%",
                statLabel: "Uptime SLA",
              },
              {
                num: "03",
                title: "Security",
                desc: "SOC2, HIPAA, and ISO 27001 compliant processes with full data sovereignty.",
                stat: "SOC2",
                statLabel: "Certified",
              },
              {
                num: "04",
                title: "Velocity",
                desc: "Follow-the-sun development model for continuous delivery and zero downtime.",
                stat: "24/7",
                statLabel: "Operations",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="group relative p-7 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-[#FDA400]/30 hover:bg-white/[0.06] transition-all duration-500 overflow-hidden"
              >
                {/* Top accent line */}
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#FDA400] to-[#f5b800] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Large background number */}
                <span className="absolute -top-3 -right-2 text-7xl font-black text-white/[0.03] group-hover:text-white/[0.06] transition-colors duration-500 select-none leading-none">
                  {item.num}
                </span>

                <div className="relative z-10">
                  <div className="text-xs font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-4">
                    {item.num}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 tracking-tight">
                    {item.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-6">
                    {item.desc}
                  </p>

                  {/* Stat pill */}
                  <div className="pt-4 border-t border-white/[0.06]">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-black text-white">{item.stat}</span>
                      <span className="text-xs text-slate-500 uppercase tracking-wider">{item.statLabel}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners with Innovators */}
      <section className="py-24 relative overflow-hidden bg-slate-50">
        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Text */}
            <div className="space-y-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-px w-10 bg-[#FDA400]" />
                <span className="text-[#1C2B6E] text-xs font-semibold uppercase tracking-[0.2em]">
                  Technology Partners
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                Partners with <br />
                <span className="text-[#FDA400]">Innovators</span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed font-light max-w-lg">
                We believe the best solutions are born from collaboration. We partner with the world's leading technology platforms and forward-thinking enterprises to co-create value.
              </p>
              <Link to="/partners">
                <Button className="bg-[#1C2B6E] hover:bg-[#162258] text-white font-semibold text-sm px-7 py-5 h-auto rounded-lg transition-all mt-4">
                  Our Partnerships
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            {/* Right: Two auto-scrolling columns */}
            <div className="relative h-[480px] overflow-hidden">
              {/* Top & bottom fade masks */}
              <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-slate-50 to-transparent z-10 pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-slate-50 to-transparent z-10 pointer-events-none" />

              <div className="grid grid-cols-2 gap-4 h-full">
                {/* Column 1: scrolls UP */}
                <div className="overflow-hidden">
                  <div
                    className="flex flex-col gap-4"
                    style={{ animation: "scrollUp 25s linear infinite" }}
                  >
                    {[...Array(2)].map((_, setIdx) => (
                      <div key={setIdx} className="flex flex-col gap-4">
                        {[
                          { name: "Salesforce", logo: "/salesforce.png" },
                          { name: "Snowflake", logo: "/snowflake.png" },
                          { name: "Tableau", logo: "/tableau.png" },
                        ].map((partner) => (
                          <div
                            key={`${setIdx}-${partner.name}`}
                            className="flex items-center justify-center h-32 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all duration-300"
                          >
                            <img
                              src={partner.logo}
                              alt={partner.name}
                              className="h-12 w-auto object-contain max-w-[80%]"
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Column 2: scrolls DOWN */}
                <div className="overflow-hidden">
                  <div
                    className="flex flex-col gap-4"
                    style={{ animation: "scrollDown 25s linear infinite" }}
                  >
                    {[...Array(2)].map((_, setIdx) => (
                      <div key={setIdx} className="flex flex-col gap-4">
                        {[
                          { name: "Sitecore", logo: "/sitecore.png" },
                          { name: "Slack", logo: "/slack.png" },
                          { name: "Drata", logo: "/Drata.jpeg" },
                        ].map((partner) => (
                          <div
                            key={`${setIdx}-${partner.name}`}
                            className="flex items-center justify-center h-32 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all duration-300"
                          >
                            <img
                              src={partner.logo}
                              alt={partner.name}
                              className="h-12 w-auto object-contain max-w-[80%]"
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inspired Values */}
      <section className="py-28 relative overflow-hidden bg-[#0f1629]">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(28,43,110,0.15),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_80%_100%,rgba(253,164,0,0.04),transparent_50%)]" />
        </div>

        <div className="section-container relative z-10">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-20">
            <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
              What Drives Us
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6">
              Inspired{" "}
              <span className="text-[#FDA400]">
                Values
              </span>
            </h2>
            <p className="text-slate-400 text-lg font-light leading-relaxed">
              The core principles that drive our culture, our decisions, and our impact.
            </p>
          </div>

          {/* Values grid */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                num: "01",
                title: "Curiosity",
                desc: "We never stop asking 'what if'. Curiosity is the fuel of our innovation. We explore, experiment, and push boundaries to find better solutions.",
              },
              {
                num: "02",
                title: "Integrity",
                desc: "We do the right thing, even when no one is watching. Trust is our currency. Transparency and honesty guide every decision we make.",
              },
              {
                num: "03",
                title: "Impact",
                desc: "We measure success not by output, but by tangible outcomes for our clients. Every line of code and every strategy serves a purpose.",
              },
            ].map((value, i) => (
              <div
                key={i}
                className="group relative rounded-2xl overflow-hidden bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-500"
              >
                {/* Top accent bar */}
                <div className="h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800]" />

                {/* Background glow on hover */}
                <div className="absolute -bottom-20 -right-20 w-60 h-60 bg-[#FDA400]/10 rounded-full blur-[80px] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                {/* Large background number */}
                <span className="absolute top-6 right-6 text-8xl font-black text-white/[0.03] group-hover:text-white/[0.06] transition-colors duration-500 select-none leading-none">
                  {value.num}
                </span>

                <div className="relative z-10 p-8 md:p-10">
                  <div className="text-xs font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-6">
                    {value.num}
                  </div>

                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 tracking-tight">
                    {value.title}
                  </h3>

                  <p className="text-slate-400 leading-relaxed">
                    {value.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visionary Guidance */}
      <section className="py-28 relative overflow-hidden bg-white">
        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            {/* Left: Text */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-10 bg-[#FDA400]" />
                <span className="text-[#1C2B6E] text-xs font-semibold uppercase tracking-[0.2em]">
                  Our Leadership
                </span>
              </div>

              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#1C2B6E] tracking-tight leading-[1.1] mb-8">
                Visionary Guidance
                <br />
                by <span className="text-[#FDA400]">Leadership</span>
              </h2>

              <p className="text-lg text-slate-500 leading-relaxed font-light max-w-lg mb-6">
                True leadership is not about being in charge. It is about taking care of those in your charge.
              </p>

              <p className="text-lg text-slate-500 leading-relaxed font-light max-w-lg mb-10">
                At RSA Tech Group, our leadership team is dedicated to fostering a culture of empowerment, where every voice is heard and every idea has the potential to change the world.
              </p>

              <Link to="/leadership-team">
                <Button className="bg-[#1C2B6E] hover:bg-[#162258] text-white font-semibold text-sm px-7 py-5 h-auto rounded-lg transition-all">
                  Meet The Full Team
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            {/* Right: Leader portrait mosaic */}
            <div className="relative">
              {/* Decorative accent behind the grid */}
              <div className="absolute -top-6 -right-6 w-48 h-48 bg-[#FDA400]/10 rounded-full blur-[80px]" />
              <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#1C2B6E]/10 rounded-full blur-[60px]" />

              <div className="grid grid-cols-6 grid-rows-4 gap-3 h-[480px] relative">
                {/* Large: Udaya (top-left, 3 cols x 2 rows) */}
                <div className="col-span-3 row-span-2 rounded-2xl overflow-hidden relative group">
                  <img
                    src="/udaya-vanteru.jpg"
                    alt="Udaya Vanteru"
                    className="w-full h-full object-cover object-[50%_15%] group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C2B6E]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white font-bold text-sm">Udaya Vanteru</p>
                    <p className="text-[#FDA400] text-xs">President</p>
                  </div>
                </div>

                {/* Srini (top-right, 3 cols x 2 rows) */}
                <div className="col-span-3 row-span-2 rounded-2xl overflow-hidden relative group">
                  <img
                    src="/Srinivas Ragipally.jpg"
                    alt="Srini Ragipally"
                    className="w-full h-full object-cover object-[50%_15%] group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C2B6E]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white font-bold text-sm">Srini Ragipally</p>
                    <p className="text-[#FDA400] text-xs">CEO / Interim COO</p>
                  </div>
                </div>

                {/* Vijay (bottom-left, 2 cols x 2 rows) */}
                <div className="col-span-2 row-span-2 rounded-2xl overflow-hidden relative group">
                  <img
                    src="/vijay-simhachalam.png"
                    alt="Vijay Simhachalam"
                    className="w-full h-full object-cover object-[50%_15%] group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C2B6E]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white font-bold text-sm">Vijay Simhachalam</p>
                    <p className="text-[#FDA400] text-xs">Director of Global Operations</p>
                  </div>
                </div>

                {/* Santthosh (bottom-center, 2 cols x 2 rows) */}
                <div className="col-span-2 row-span-2 rounded-2xl overflow-hidden relative group">
                  <img
                    src="/IMG_6220.jpg"
                    alt="Santthosh Purmani"
                    className="w-full h-full object-cover object-[50%_25%] group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C2B6E]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white font-bold text-sm">Santthosh Purmani</p>
                    <p className="text-[#FDA400] text-xs">CIO / CMO</p>
                  </div>
                </div>

                {/* Suhaib (bottom-right, 2 cols x 2 rows) */}
                <div className="col-span-2 row-span-2 rounded-2xl overflow-hidden relative group">
                  <img
                    src="/suhaib-ahmed.png"
                    alt="Suhaib Ahmed"
                    className="w-full h-full object-cover object-[50%_20%] group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C2B6E]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white font-bold text-sm">Suhaib Ahmed</p>
                    <p className="text-[#FDA400] text-xs">Regional IT Sales Head - APAC/EMA</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Companies */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-slate-50">
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="section-container relative z-10">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#FDA400]" />
              <span className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em]">Our Companies</span>
              <div className="h-px w-10 bg-[#FDA400]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
              The RSA Tech Group <span className="text-[#FDA400]">Portfolio</span>
            </h2>
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="partner-card rounded-2xl p-8 md:p-10 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-bold uppercase tracking-[0.15em] px-3 py-1 rounded-full bg-[#FDA400]/15 text-[#FDA400] border border-[#FDA400]/20">Flagship</span>
              </div>
              <h3 className="text-2xl font-black text-[#1C2B6E] mb-3">ConnexR</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                <a href="https://connexr.com" target="_blank" rel="noopener" className="text-[#1C2B6E] font-semibold hover:text-[#FDA400] transition-colors underline underline-offset-2">ConnexR</a> (<a href="https://connexr.com" target="_blank" rel="noopener" className="text-[#FDA400] font-semibold hover:text-[#1C2B6E] transition-colors">connexr.com</a>) is RSA Tech Group's AI-First Managed Services and Enterprise Engineering company, serving Fortune 500 clients across Healthcare, BFSI, and Manufacturing. ConnexR delivers intelligent managed services, AIOps, AI transformation, and cloud infrastructure solutions across 9 industries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <div className="bg-white">
        <PageFAQ
          faqs={aboutFAQs}
          title="About RSA Tech Group FAQs"
          subtitle="Common questions about who we are and what we do"
          variant="light"
        />
      </div>

      <Footer />
    </div>
  )
}

export default About
